import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography, CircularProgress, Divider } from '@mui/material';
import { SalesOrder } from 'services/salesOrders';
import { Stage as KonvaStage } from 'konva/lib/Stage';
import { getTemplatesApi } from 'services/templates/api';
import {
  TemplateApiPayload,
  LabelElement,
} from 'ui/modules/reports/pages/TemplatesPage/types';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import {
  normalizeTemplate,
  generatePreviewLayout,
  PreviewLayoutConfig,
} from 'ui/modules/reports/pages/TemplatesPage/utils/templateUtils';
import jsPDF from 'jspdf';
import { logErrorCtx } from 'app/logging';
import DesignerCanvas from 'ui/modules/reports/pages/TemplatesPage/components/canvas/DesignerCanvas';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';
import { PdfExportModal } from 'ui/components/Modal/PdfExportModal';
import { Modal } from 'ui/components/Modal/Modal';
import { useReportsModalStyle } from 'ui/components/Modal/ReportsModal/styled';
import { generatePdfFilename } from './utils';

interface ReportTemplateModalProps {
  open: boolean;
  onClose: () => void;
  salesOrder: SalesOrder;
}

const ReportTemplateModal: React.FC<ReportTemplateModalProps> = ({
  open,
  onClose,
  salesOrder,
}) => {
  const [currentTemplate, setCurrentTemplate] =
    useState<TemplateApiPayload | null>(null);
  const [allTemplates, setAllTemplates] = useState<TemplateApiPayload[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [showPageBreaks, setShowPageBreaks] = useState(true);
  const [pdfFilename, setPdfFilename] = useState('');
  const [elements, setElements] = useState<LabelElement[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const stageRef = useRef<KonvaStage | null>(null);

  const classes = useReportsModalStyle();

  const isValidTemplate = (template: TemplateApiPayload | null): boolean => {
    if (!template?.template) return false;
    const normalized = normalizeTemplate(template.template);
    return (
      normalized.pages.length > 0 &&
      normalized.pages[0].elements.length > 0 &&
      normalized.pageSize.width > 0 &&
      normalized.pageSize.height > 0
    );
  };

  const handleOpenExportModal = () => {
    if (!currentTemplate || !salesOrder.number) return;
    const initialFilename = generatePdfFilename(salesOrder.number);
    setPdfFilename(initialFilename);
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleConfirmExport = (filename: string) => {
    handleExportPDF(filename);
    handleCloseExportModal();
  };

  const handleExportPDF = async (filename: string) => {
    if (!stageRef.current || !currentTemplate || !filename) {
      logErrorCtx('Stage or template not available for PDF export', {
        component: 'ReportTemplateModal',
        description: `Filename: ${filename}. Template: ${JSON.stringify(
          currentTemplate
        )}`,
      });
      return;
    }
    setIsExporting(true);

    // Store page breaks visibility state outside try block for finally access
    const pageBreaksVisible = showPageBreaks;

    try {
      const stage = stageRef.current;
      const template = normalizeTemplate(currentTemplate.template);
      const { width: pageWidth, height: pageHeight } = template.pageSize;
      const orientation = pageWidth > pageHeight ? 'l' : 'p';

      // Store original stage state
      const originalScale = stage.scale();
      const originalPosition = stage.position();

      // Hide page breaks for export
      if (pageBreaksVisible) setShowPageBreaks(false);

      // Allow Konva to re-render before exporting
      await new Promise((resolve) => setTimeout(resolve, 100));

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation,
        unit: 'px',
        format: [pageWidth, pageHeight],
      });

      // Reset stage scale and position for export
      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.draw();

      // Add 0.5 inch margins for printing
      const MARGIN_INCHES = 0.5;
      const MARGIN_PX = MARGIN_INCHES * 72; // Convert inches to points (72 points per inch)

      // Calculate content dimensions with margins
      const contentWidth = pageWidth - 2 * MARGIN_PX;
      const contentHeight = pageHeight - 2 * MARGIN_PX;

      // Export each page separately
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        // Capture each page as a separate image
        // Since margins are disabled in Sales Order Report modal, no offset needed
        const dataURL = stage.toDataURL({
          x: 0,
          y: i * pageHeight,
          width: pageWidth,
          height: pageHeight,
          pixelRatio: 2, // Higher quality
        });

        // Add the page image to PDF with margins - centered on page
        pdf.addImage(
          dataURL,
          'PNG',
          MARGIN_PX, // X offset for left margin
          MARGIN_PX, // Y offset for top margin
          contentWidth, // Scaled width (with margins)
          contentHeight // Scaled height (with margins)
        );
      }

      // Restore original stage state
      stage.scale(originalScale);
      stage.position(originalPosition);
      stage.draw();

      // Restore page breaks visibility
      if (pageBreaksVisible) setShowPageBreaks(true);

      pdf.save(`${filename}.pdf`);
    } catch (err) {
      logErrorCtx(
        'An error occurred while exporting the sales order report as a PDF.',
        { error: err instanceof Error ? err : new Error(String(err)) }
      );
      setError('Failed to export PDF. Please try again.');
    } finally {
      // Restore page breaks visibility in case of error
      if (pageBreaksVisible) setShowPageBreaks(true);
      setIsExporting(false);
    }
  };

  const handleUpdateElement = (id: string, updates: Partial<LabelElement>) => {
    setElements((prevElements) =>
      prevElements.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
  };

  // Initialize elements and pages when currentTemplate changes
  useEffect(() => {
    if (currentTemplate) {
      const normalized = normalizeTemplate(currentTemplate.template);

      // Flatten elements from all pages
      const flatElements = normalized.pages.flatMap((page, pageIndex) =>
        page.elements.map((element) => ({
          ...element,
          y: element.y + pageIndex * normalized.pageSize.height,
        }))
      );

      // Use the new generatePreviewLayout function to handle all preview logic
      const config: PreviewLayoutConfig = {
        pageSize: normalized.pageSize,
        showFooter: normalized.footerEnabled || false,
        footerHeight: normalized.footerHeight || 80,
        initialTotalPages: normalized.pages.length,
      };

      const layoutResult = generatePreviewLayout(
        flatElements,
        salesOrder,
        config
      );

      setElements(layoutResult.elements);
      setTotalPages(layoutResult.totalPages);
    }
  }, [currentTemplate, salesOrder]);

  // Memoize normalized template to prevent re-computation on each render
  const footerConfig = useMemo(() => {
    return currentTemplate ? normalizeTemplate(currentTemplate.template) : null;
  }, [currentTemplate?.template]);

  const showFooter = footerConfig?.footerEnabled ?? false;
  const footerHeight = footerConfig?.footerHeight ?? 80;

  useEffect(() => {
    if (open) {
      const fetchTemplates = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const rawTemplates = await getTemplatesApi('sales order');
          const verifiedTemplates = rawTemplates.filter((t) => {
            if (!t.template) return false;
            const hasElements = 'elements' in t.template;
            const hasPages = 'pages' in t.template;
            return (hasElements || hasPages) && t.template.pageSize;
          });

          setAllTemplates(verifiedTemplates);

          if (verifiedTemplates.length > 0) {
            const defaultTemplate = verifiedTemplates.find(
              (t) => t.defaultTemplate
            );
            setCurrentTemplate(defaultTemplate || verifiedTemplates[0]);
          } else {
            setError('No templates found for Sales Orders.');
          }
        } catch (e) {
          setError('Failed to load report templates.');
          logErrorCtx('Failed to load report templates for sales order', {
            error: e instanceof Error ? e : new Error(String(e)),
          });
        }
        setIsLoading(false);
      };

      fetchTemplates();
    }
  }, [open]);

  const isExportButtonDisabled =
    isExporting || !currentTemplate || isLoading || !!error;

  return (
    <>
      <Modal
        open={open}
        title={`Sales Order Report: ${salesOrder.number}`}
        applyLabel="Submit"
        onCancelClicked={onClose}
        withoutDefaultPadding
        fullScreen
        className={classes.reportsModal}
        dataQa="report-template-modal"
        ModalActionsComponent={() => <></>}
      >
        <Box
          sx={{
            display: 'flex',
            height: '100%', // Fixed height for the modal content
            borderTop: '1px solid #EBEBEB',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              bgcolor: 'grey.100',
              borderRight: '1px solid #EBEBEB',
              overflow: 'hidden',
            }}
          >
            {!isLoading &&
              !error &&
              currentTemplate &&
              (isValidTemplate(currentTemplate) ? (
                <DesignerCanvas
                  stageRef={stageRef}
                  elements={elements}
                  selectedElement={null}
                  setSelectedElement={() => {}}
                  selectedElements={[]}
                  setSelectedElements={() => {}}
                  onUpdateElement={handleUpdateElement}
                  isDragging={false}
                  previewMode={true}
                  previewData={salesOrder}
                  showGrid={false}
                  showPageBreaks={showPageBreaks}
                  showMargins={false}
                  canvasWidth={
                    normalizeTemplate(currentTemplate.template).pageSize.width
                  }
                  canvasHeight={
                    normalizeTemplate(currentTemplate.template).pageSize
                      .height * totalPages
                  }
                  totalPages={totalPages}
                  noPadding={true}
                  showFooter={showFooter}
                  footerHeight={footerHeight}
                  footerPage={totalPages - 1}
                />
              ) : (
                <Typography color="error">
                  The report template is empty or has invalid dimensions. Please
                  check the default template configuration.
                </Typography>
              ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '250px',
              minWidth: '250px', // Prevent shrinking
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ mt: 4, mx: 2 }}>
              {allTemplates.length > 0 && !isLoading && !error && (
                <Autocomplete
                  options={allTemplates}
                  getOptionLabel={(option) =>
                    option.name + (option.defaultTemplate ? ' (default)' : '')
                  }
                  value={currentTemplate ?? undefined}
                  onChange={(_, newValue) => {
                    setCurrentTemplate(newValue);
                  }}
                  label="Select Template"
                  disableClearable
                  autoSelect={false}
                  sx={{ mt: 2 }}
                />
              )}
              {isLoading && !error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
            </Box>

            <Box>
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 2,
                  px: 2,
                }}
              >
                <FBOButton
                  variant="primary"
                  color="positive"
                  onClick={handleOpenExportModal}
                  loading={isExporting}
                  disabled={isExportButtonDisabled}
                  data-qa="export-pdf-button"
                  icon="download"
                >
                  Download PDF
                </FBOButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
      <PdfExportModal
        open={isExportModalOpen}
        title="Export PDF"
        initialFilename={pdfFilename}
        isLoading={isExporting}
        onConfirm={handleConfirmExport}
        onClose={handleCloseExportModal}
      />
    </>
  );
};

export default ReportTemplateModal;
